/**
 * @file EditSalaryType.jsx
 * @description Component for editing an existing Salary Type.
 * It handles fetching the specific record and submitting updates.
 */

import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GetById, Update } from "adminApi/SalaryTypeApi/SalaryType";
import Spinner from "Common/Globle/Spinner";
import EditSalaryTypeForm from "./components/EditSalaryTypeForm";
import fields from "../json/SalaryType.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchFormDataById from "Hooks/useFetchFormDataById";
import { SalaryTypeRules } from "validation/ValidationRules";
import { validateForm } from "utils/validation";

const EditSalaryType = () => {
    const { id } = useParams(); // Get salary_type_id from URL params
    const navigate = useNavigate();
    const { formdata } = fields;

    // Default initial values before data is fetched
    const initialValues = formdata.reduce((acc, field) => {
        acc[field.name] = field.name === "status" ? "1" : field.name === "default_amount" ? "0" : "";
        return acc;
    }, {});

    /** 
     * useFormChange hook to manage the editable form state.
     */
    const {
        values,
        errors,
        handleChange,
        setValues,
        setErrors
    } = useFormChange(initialValues, SalaryTypeRules);

    /**
     * useFetchFormDataById hook performs the API call to get existing data 
     * and populates the form (setValues).
     */
    const { loading: fetchLoading } = useFetchFormDataById(id, formdata, GetById, setValues);

    /**
     * useFormSubmit hook handles the update API call.
     */
    const { loading: submitLoading, handleSubmit } = useFormSubmit(
        async (formValues) => {
            const formData = new FormData();
            Object.entries(formValues).forEach(([key, val]) => formData.append(key, val ?? ""));
            // Return the update API call promise
            return await Update({ id, data: formData });
        },
        (vals) => {
            // Validation before submission
            const err = validateForm(vals, SalaryTypeRules);
            setErrors(err);
            return err;
        },
        () => navigate(-1), // On success: go back to list
        "Salary Type updated successfully"
    );

    return (
        <>
            {/* Show spinner during fetching or submission */}
            {(fetchLoading || submitLoading) && <Spinner isSpinner />}

            {/* 
               Only render the form once data has been successfully fetched 
               to ensure initial values are correctly set.
            */}
            {!fetchLoading && (
                <EditSalaryTypeForm
                    fields={fields}
                    values={values}
                    errors={errors}
                    handleChange={handleChange}
                    handleSubmit={(e) => handleSubmit(e, values)}
                    loading={submitLoading}
                    title="Edit Salary Type"
                />
            )}
        </>
    );
};

export default EditSalaryType;

